<template>
  <div v-if="info && info.activity_count > 0" class="exp-city-info">
    <h2 class="exp-city-info__title">{{ $t('About Experiences in') }} {{ info.city_name }}</h2>

    <p class="exp-city-info__intro">
      {{ introText }}
    </p>

    <!-- Category grid -->
    <div v-if="info.top_categories?.length" class="exp-city-info__categories">
      <h3 class="exp-city-info__subtitle">{{ $t('Popular Categories') }}</h3>
      <div class="exp-city-info__category-grid">
        <nuxt-link
          v-for="cat in info.top_categories"
          :key="cat.code"
          :to="`/experiences/${citySlug}?categories=${cat.code}`"
          class="exp-city-info__category-chip"
        >
          {{ cat.name }} <span class="exp-city-info__category-count">({{ cat.count }})</span>
        </nuxt-link>
      </div>
    </div>

    <!-- Top-rated activities -->
    <div v-if="info.top_activities?.length" class="exp-city-info__top">
      <h3 class="exp-city-info__subtitle">{{ $t('Top Rated Experiences') }}</h3>
      <div class="exp-city-info__top-grid">
        <nuxt-link
          v-for="act in info.top_activities"
          :key="act.id"
          :to="`/experiences/${citySlug}/${act.id}-${act.slug}`"
          class="exp-city-info__activity-card"
        >
          <img
            v-if="act.cover_image_url"
            :src="imageUrlWithProxy(act.cover_image_url, 280, 180, 'fill')"
            :alt="act.name"
            class="exp-city-info__activity-img"
            loading="lazy"
            width="280"
            height="180"
          />
          <div class="exp-city-info__activity-body">
            <span class="exp-city-info__activity-name">{{ act.name }}</span>
            <span class="exp-city-info__activity-meta">
              <span v-if="act.avg_rating" class="exp-city-info__rating">{{ act.avg_rating }}/5</span>
              <span v-if="act.price_from" class="exp-city-info__price">{{ $t('from') }} US${{ Math.round(act.price_from) }}</span>
            </span>
          </div>
        </nuxt-link>
      </div>
    </div>

    <!-- FAQ section -->
    <div v-if="faqItems.length" class="exp-city-info__faq">
      <h3 class="exp-city-info__subtitle">{{ $t('Frequently Asked Questions') }}</h3>
      <div v-for="(item, i) in faqItems" :key="i" class="exp-city-info__faq-item">
        <h4 class="exp-city-info__faq-q">{{ item.title }}</h4>
        <p class="exp-city-info__faq-a">{{ item.text }}</p>
      </div>
    </div>

    <!-- Cross-links -->
    <div class="exp-city-info__links">
      <nuxt-link v-if="info.hotel_count > 0" :to="`/hotels/${citySlug}`" class="exp-city-info__cross-link">
        {{ $t('Browse') }} {{ info.hotel_count }} {{ $t('hotels in') }} {{ info.city_name }}
      </nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { imageUrlWithProxy } from '~/utils/imageproxy'

interface TopCategory {
  code: string
  name: string
  count: number
}

interface TopActivity {
  id: number
  name: string
  slug: string
  avg_rating: number | null
  price_from: number | null
  cover_image_url: string | null
  duration: number | null
  category_codes: string[]
}

export interface ExperienceCityInfo {
  city_name: string
  country_name: string
  country_code: string
  latitude: number | null
  longitude: number | null
  activity_count: number
  price_min: number | null
  price_max: number | null
  avg_rating: number | null
  free_cancellation_pct: number
  top_categories: TopCategory[]
  top_activities: TopActivity[]
  hotel_count: number
  ai_description: string | null
  ai_meta_description: string | null
  ai_faq: Array<{ question: string; answer: string }> | null
}

const props = defineProps<{
  citySlug: string
  info: ExperienceCityInfo | null
}>()

const introText = computed(() => {
  if (!props.info) return ''
  const c = props.info

  // Prefer AI-generated description
  if (c.ai_description) return c.ai_description

  // Fallback to template-based text
  let text = `Discover ${c.activity_count} tours, activities and things to do in ${c.city_name}, ${c.country_name}`

  if (c.price_min && c.price_max) {
    text += `, with prices ranging from US$${Math.round(c.price_min)} to US$${Math.round(c.price_max)}`
  }

  if (c.avg_rating) {
    text += `. The average experience rating is ${c.avg_rating} out of 5`
  }

  if (c.top_categories?.length) {
    const catNames = c.top_categories.slice(0, 3).map(cat => cat.name)
    text += `. Popular categories include ${catNames.join(', ')}`
  }

  text += '.'
  return text
})

const faqItems = computed(() => {
  if (!props.info) return []
  const c = props.info

  // Prefer AI-generated FAQ
  if (c.ai_faq?.length) {
    return c.ai_faq.map(item => ({ title: item.question, text: item.answer }))
  }

  // Fallback to template-based FAQ
  const items: Array<{ title: string; text: string }> = []

  if (c.top_activities?.length) {
    const topNames = c.top_activities.slice(0, 3).map(a => a.name).join(', ')
    items.push({
      title: `What are the best things to do in ${c.city_name}?`,
      text: `Top-rated experiences in ${c.city_name} include ${topNames}.`,
    })
  }

  if (c.price_min && c.price_max) {
    items.push({
      title: `How much do activities cost in ${c.city_name}?`,
      text: `Activities in ${c.city_name} range from US$${Math.round(c.price_min)} to US$${Math.round(c.price_max)} per person.`,
    })
  }

  if (c.top_categories?.length) {
    const catList = c.top_categories.slice(0, 5).map(cat => `${cat.name} (${cat.count})`).join(', ')
    items.push({
      title: `What are the most popular types of experiences in ${c.city_name}?`,
      text: `The most popular categories are ${catList}.`,
    })
  }

  if (c.free_cancellation_pct > 0) {
    items.push({
      title: `Can I get free cancellation on tours in ${c.city_name}?`,
      text: `Yes, ${c.free_cancellation_pct}% of activities in ${c.city_name} offer free cancellation.`,
    })
  }

  items.push({
    title: `How many tours and activities are available in ${c.city_name}?`,
    text: `There are ${c.activity_count} tours, activities and experiences available in ${c.city_name}.`,
  })

  return items
})
</script>

<style scoped>
.exp-city-info {
  margin-top: 32px;
  padding: 24px;
  background-color: var(--light-grey, #f8f9fa);
  border-radius: 12px;
}

.exp-city-info__title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
}

.exp-city-info__subtitle {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 12px;
}

.exp-city-info__intro {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-secondary, #444);
  margin-bottom: 20px;
}

/* Category grid */
.exp-city-info__categories {
  margin-bottom: 24px;
}

.exp-city-info__category-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.exp-city-info__category-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  background: var(--white, #fff);
  border: 1px solid var(--light-grey-border, #e0e0e0);
  border-radius: 20px;
  font-size: 14px;
  color: var(--black, #222);
  text-decoration: none;
  transition: border-color 0.15s, background-color 0.15s;
}

.exp-city-info__category-chip:hover {
  border-color: var(--blue, #1985f9);
  background-color: rgba(25, 133, 249, 0.04);
}

.exp-city-info__category-count {
  color: var(--dark-grey, #666);
  font-size: 13px;
}

/* Top-rated activities */
.exp-city-info__top {
  margin-bottom: 24px;
}

.exp-city-info__top-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.exp-city-info__activity-card {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  background: var(--white, #fff);
  border: 1px solid var(--light-grey-border, #e0e0e0);
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s;
}

.exp-city-info__activity-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.exp-city-info__activity-img {
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.exp-city-info__activity-body {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.exp-city-info__activity-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.exp-city-info__activity-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--dark-grey, #666);
}

.exp-city-info__rating {
  font-weight: 600;
  color: var(--green, #2d9c3e);
}

.exp-city-info__price {
  font-weight: 500;
}

/* FAQ */
.exp-city-info__faq {
  margin-bottom: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--light-grey-border, #e0e0e0);
}

.exp-city-info__faq-item {
  margin-bottom: 14px;
}

.exp-city-info__faq-q {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.exp-city-info__faq-a {
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary, #444);
}

/* Cross-links */
.exp-city-info__links {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--light-grey-border, #e0e0e0);
}

.exp-city-info__cross-link {
  display: inline-flex;
  align-items: center;
  color: var(--blue, #1985f9);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
}

.exp-city-info__cross-link:hover {
  text-decoration: underline;
}

@media screen and (max-width: 768px) {
  .exp-city-info__top-grid {
    grid-template-columns: 1fr;
  }
}
</style>
